import React, { useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View, ViewStyle, Platform } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { MODULE_COLORS } from '@cubemeet/core';

/**
 * ProjectDescription 组件属性
 */
export interface ProjectDescriptionProps {
  /**
   * 项目描述内容
   */
  description: string;
  /**
   * 是否可以编辑
   */
  canEdit?: boolean;
  /**
   * 编辑回调
   */
  onEdit?: () => void;
  /**
   * 翻译函数
   */
  t?: (key: string) => string;
  /**
   * 自定义样式
   */
  style?: ViewStyle;
  /**
   * 初始展开状态
   */
  initialExpanded?: boolean;
  /**
   * 最大显示行数（收起时）
   */
  maxLines?: number;
}

const defaultT = (key: string) => {
  const translations: Record<string, string> = {
    'travelDetail.content.projectDescription': '项目描述',
    'travelDetail.content.itineraryOverview': '行程总览',
    'travelDetail.content.expand': '展开',
    'travelDetail.content.collapse': '收起',
  };
  return translations[key] || key;
};

/**
 * ProjectDescription 组件
 * 
 * 用于显示项目简介/行程概览的共享组件
 */
const ProjectDescription: React.FC<ProjectDescriptionProps> = ({
  description,
  canEdit = false,
  onEdit,
  t = defaultT,
  style,
  initialExpanded = false,
  maxLines = 4,
}) => {
  const [isExpanded, setIsExpanded] = useState(initialExpanded);

  // 判断是否有更多内容
  const hasMore = () => {
    if (!description) return false;
    try {
      const lines = description.split('\n').filter(Boolean).length;
      if (lines > maxLines) return true;
      if (description.length > 160) return true;
      return false;
    } catch (err) {
      return description.length > 160;
    }
  };

  const shouldShowExpandButton = hasMore();

  return (
    <View style={[styles.container, style]}>
      <View style={styles.header}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <View style={{ marginRight: 8 }}>
            <Icon name="description" size={18} color={MODULE_COLORS.travel} />
          </View>
          <Text style={styles.title}>{t('travelDetail.content.projectDescription')}</Text>
        </View>
        {canEdit && onEdit && (
          <TouchableOpacity style={styles.editButton} onPress={onEdit}>
            <Icon name="description" size={16} color={MODULE_COLORS.travel} />
            <Text style={styles.editButtonText}>{t('travelDetail.content.itineraryOverview')}</Text>
          </TouchableOpacity>
        )}
      </View>
      
      {description ? (
        <Text
          style={styles.description}
          numberOfLines={isExpanded ? undefined : maxLines}
        >
          {description}
        </Text>
      ) : (
        <Text style={[styles.description, { color: '#9CA3AF', fontStyle: 'italic' }]}>
          {t('travelDetail.content.noDescription') || '暂无描述'}
        </Text>
      )}
      
      {shouldShowExpandButton && (
        <TouchableOpacity
          style={styles.expandButton}
          onPress={() => setIsExpanded(!isExpanded)}
        >
          <Text style={styles.expandButtonText}>
            {isExpanded ? t('travelDetail.content.collapse') : t('travelDetail.content.expand')}
          </Text>
          <Icon
            name={isExpanded ? 'expand-less' : 'expand-more'}
            size={16}
            color={MODULE_COLORS.travel}
          />
        </TouchableOpacity>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    // Use CSS boxShadow on web to avoid react-native-web deprecation warnings for shadow* props
    ...(Platform.select ? Platform.select({ web: { boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }, default: { shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 2, elevation: 1 } }) : { shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 2, elevation: 1 }),
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  editButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: 'rgba(255, 102, 0, 0.05)',
    borderRadius: 20,
    borderWidth: 1,
    borderColor: MODULE_COLORS.travel,
  },
  editButtonText: {
    marginLeft: 4,
    fontSize: 12,
    color: MODULE_COLORS.travel,
    fontWeight: '500',
  },
  description: {
    fontSize: 14,
    color: '#374151',
    lineHeight: 22,
  },
  expandButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 12,
    marginTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#f3f4f6',
  },
  expandButtonText: {
    fontSize: 14,
    color: MODULE_COLORS.travel,
    fontWeight: '500',
    marginRight: 4,
  },
});

export default ProjectDescription;
